<template>
  <div
    class="review mx-auto"
    :itemprop="SCHEMA_ITEM_PROP.REVIEW"
    itemscope
    :itemtype="SCHEMA_ITEM_TYPE.REVIEW"
  >
    <ReviewAuthor
      :author="author"
      :time-ago="timeAgo"
      :source="source"
    />
    <div class="review__content">
      <div>
        <ReviewsStars
          class="mt-1 lg:mt-0 mb-6"
          :rating="rating"
          :show-schema="true"
        />
        <strong
          :itemprop="SCHEMA_ITEM_PROP.NAME"
        >{{ title }}</strong>
        <p
          class="mt-2"
          :itemprop="SCHEMA_ITEM_PROP.REVIEW_BODY"
          :class="{'italic': isReviewEmpty}"
        >
          {{ message }}
        </p>
        <meta
          :itemprop="SCHEMA_ITEM_PROP.DATE_PUBLISHED"
          :content="dateToYYYYMMDD(date)"
        >
      </div>
      <div
        v-if="response"
        class="border-l-4 border-EXPONDO-grey-20 mt-3 md:mt-6 pt-2 pb-2 pl-6"
      >
        <div class="flex items-center">
          <p class="text-sm">
            {{ $t("Reply from") }}
          </p>
          <img
            class="ml-3 max-h-5 h-auto"
            alt="Expondo Logo"
            :src="EXPONDO_LOGO_DARK_SIMPLE_PATH"
          >
        </div>
        <div
          class="mt-5"
          v-html="response"
        />
      </div>
      <div
        v-if="showTranslateButton"
        class="text-EXPONDO-secondary text-xs inline-block cursor-pointer mt-4"
        @click="triggerTranslation"
      >
        {{ translationTogglerText }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">


import type { ProductReview } from '~~/types/product/attribute/ProductReview';
import { SCHEMA_ITEM_TYPE, SCHEMA_ITEM_PROP } from '~~/constants/seo';
import { dateToYYYYMMDD } from '~~/helpers/time/dateToYYYYMMDD/dateToYYYYMMDD';
import { EXPONDO_LOGO_DARK_SIMPLE_PATH } from '~~/constants/expondoLogoPath';
import { getTimeAgo } from '~~/helpers/time/getTimeAgo';
import { capitalizeString } from '~~/helpers/capitalizeString';
import localizationDefinitions from '~~/i18n/localizationDefinitions';
import { reviewGetters } from '~/utils/ct';

export default defineComponent({
  name: 'ReviewWrapper',

  props: {
    review: {
      type: Object as PropType<ProductReview>,
      required: true,
    },
  },
  setup(props: {review: ProductReview}) {

    const { languageAndCountry: activeLocale } = useLocalization();
    const relativeTime = computed(() => new Intl.RelativeTimeFormat(activeLocale.value, { style: 'long', numeric: 'auto' }));
    const date = computed(() => props?.review?.custom?.reviewCreatedAt?.[0]?.value || props?.review?.createdAt);
    const { time, unit } = getTimeAgo(date.value ? new Date(date.value) : undefined);
    const timeAgo = computed(() => time && unit ? relativeTime.value?.format(time, unit) : '');
    const i18n = useI18n();

    const source = computed(() => {

      const countryLocalization = localizationDefinitions.getByLanguageAndCountry(props.review.locale);
      const capitalizedWord = capitalizeString(props.review.custom?.reviewSource?.[0]?.value, '_');

       
      return i18n.t('Posted on X', { _reviewSource_: capitalizedWord, _locale_: countryLocalization.label });
    
    });

    const isTranslatedReviewShowed = ref(true);
    const translationTogglerText = computed(() => isTranslatedReviewShowed.value ? i18n.t('Show original review') : i18n.t('Show translated review'));

    const originalTitle = computed(() => props.review?.title);
    const translatedTitle = computed(() => props.review.custom?.titleTranslations?.[0]?.value?.[activeLocale.value]);
    const originalMessage = computed(() => reviewGetters.getReviewMessage(props.review));
    const translatedMessage = computed(() => props.review.custom?.textTranslations?.[0]?.value?.[activeLocale.value]);
    const title = computed(() => isTranslatedReviewShowed.value && translatedTitle.value ? translatedTitle.value : originalTitle.value);
    const messageText = computed(() => isTranslatedReviewShowed.value && translatedMessage.value ? translatedMessage.value : originalMessage.value);
    const isReviewEmpty = computed(() => !originalMessage.value);
    const defaultTranslatedMessage = computed(() =>
      isTranslatedReviewShowed.value
        ? props.review.custom?.textTranslations?.[0]?.value?.[activeLocale.value]
        : props.review.custom?.textTranslations?.[0]?.value?.[props.review.locale],
    );
    const response = computed(() =>
      isTranslatedReviewShowed.value
        ? props.review.custom?.answerTranslations?.[0]?.value?.[activeLocale.value]
        : props.review.custom?.customerCareResponseText?.[0]?.value,
    );

    const message = computed(() => !isReviewEmpty.value ? messageText.value : defaultTranslatedMessage.value);

    const isReviewLocaleSameAsActiveLocale = computed(() => props.review.locale === activeLocale.value);
    const isWholeReviewTranslated = computed(() => translatedTitle.value && translatedMessage.value);
    const isDefaultReviewWithoutTitle = computed(() => !originalTitle.value && defaultTranslatedMessage.value);

    const showTranslateButton = computed(() => {

      return !isReviewLocaleSameAsActiveLocale.value && (isWholeReviewTranslated.value || isDefaultReviewWithoutTitle.value);
    
    });

    const triggerTranslation = () => {

      isTranslatedReviewShowed.value = !isTranslatedReviewShowed.value; 

    };

    return {
      rating: reviewGetters.getReviewRating(props.review),
      author: reviewGetters.getReviewAuthor(props.review),
      message,
      isReviewEmpty,
      timeAgo,
      date,
      source,
      title,
      response,
      dateToYYYYMMDD,
      SCHEMA_ITEM_TYPE,
      SCHEMA_ITEM_PROP,
      EXPONDO_LOGO_DARK_SIMPLE_PATH,
      triggerTranslation,
      translationTogglerText,
      showTranslateButton,
    };
  
  },
});

</script>

<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
  $review-max-width: 1000px;
  $review-author-max-width: 250px;
  $review-content-max-width: 700px;

  .review {
    max-width: $review-max-width;

    @include breakpoints.for-desktop {
      display: flex;
    }

    &__author {
      max-width: $review-author-max-width;
      width: 100%;
      padding-right: 15px;
      flex-shrink: 0;
    }

    &__content {
      max-width: $review-content-max-width;
    }
  }
</style>
